import { useFeedback, useMemoAsync, useTranslation } from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { useNavigate } from "@opendash/router";
import {
  Button,
  Divider,
  Flex,
  Form,
  Modal,
  Steps,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";
import {
  NextScheduleTag,
  StepDescription,
  StepExecuteContent,
  StepsExecuteFormValues,
  StepsObjectInScheduleExecution,
  useFilteredStepsByRoles,
} from "..";
import { SelectMedia } from "../../../_shared";
import {
  Item,
  Media,
  Schedule_Execution_Step,
} from "../../../js/parsegenerated";
import { Schedule_Execution } from "../../../js/parsegenerated/Schedule_Execution";
import { Material } from "../../../types/Material";
import { TicketSaveModal } from "../../tickets";
import ItemSelectionList, {
  ItemSelectionList_FormValues,
} from "./ItemSelectionList";

const { Title } = Typography;

export const StepsExecute = observer(
  (props: {
    schedule: Schedule_Execution | undefined;
    userroles: Parse.Object[];
  }) => {
    const navigate = useNavigate();
    const { message } = useFeedback();
    const t = useTranslation();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [form] = Form.useForm<ItemSelectionList_FormValues>();
    const [steps, updateStep, stepsUpdated] = useFilteredStepsByRoles(
      props.schedule
    );
    const [ticketModalOpen, setTicketModalOpen] = useState<boolean>(false);
    const [parseFiles, setParseFiles] = useState<Parse.File[]>([]);

    const sourceQuery = new Parse.Query(Source).equalTo(
      "objectId",
      props.schedule?.get("source")?.id
    );

    const foundItems = useMemoAsync(
      () => new Parse.Query(Item).matchesQuery("sources", sourceQuery).find(),
      [props.schedule?.get("source")?.id],
      []
    );

    useEffect(() => {
      async function fetchMedia() {
        const mediaFiles = await props.schedule
          ?.relation("media")
          .query()
          .include("file")
          .find();
        setParseFiles(mediaFiles?.map((media) => media.get("file")) || []);
      }

      fetchMedia();
    }, [props.schedule]);

    const buttons = useMemo(() => {
      const onSave = async () => {
        if (props.schedule) {
          // save materials
          if (foundItems.length > 0) {
            const materialValues = await form.validateFields();
            const materialSelection: { selected: Material }[] =
              materialValues.items[props.schedule.get("source").id];
            if (materialSelection) {
              const materials = materialSelection.map((item) => item.selected);
              props.schedule.set("material", materials);
            }
          }

          //Check if all steps are done, if so set finishedAt to now
          const alldone = steps.every((step, index) => step.done);
          if (alldone) {
            props.schedule.set("finishedAt", new Date(dayjs().toISOString()));
          }

          const currentMedia = props.schedule.relation("media");
          const mediaFiles = await currentMedia.query().find();

          currentMedia.remove(mediaFiles);
          await props.schedule.save();

          const newMedia = props.schedule.relation("media");

          const newMediaFiles = (parseFiles || []).map(
            (file) => new Media({ media: file })
          );

          const savedMediaFiles = await Media.saveAll(newMediaFiles);

          for (const media of savedMediaFiles) {
            newMedia.add(media);
          }
          await props.schedule.save();

          // Save Steps with comments
          for await (const step of steps) {
            //First find the step in Schedule_Execution_Step by id
            const stepExecution = await new Parse.Query(
              Schedule_Execution_Step
            ).get(step.id);
            stepExecution.set("done", step.done);
            stepExecution.set("comment", step.comment);

            await stepExecution.save();
          }

          // Save Steps with comments
          message.success(t("maintenance:global.messages.save.success"), 10);
          navigate("/openservice/schedule/overview");
        }
      };

      const handleSendAnyways = () => {
        Modal.confirm({
          title: "Bestätigung",
          content: "Möchten Sie wirklich trotzdem absenden?",
          okText: "Ja, absenden",
          cancelText: "Abbrechen",
          onOk: onSave,
        });
      };

      return (
        <Flex vertical={false} gap={16} style={{ marginTop: 24 }}>
          <Tooltip
            title={
              !steps.every((step) => step.done)
                ? "Die einzelnen Arbeitsschritte müssen als Erledigt markiert werden"
                : ""
            }
          >
            <Button
              style={{ flexBasis: "80%", flexGrow: 1 }}
              type="primary"
              block
              disabled={!steps.every((step) => step.done)}
              onClick={onSave}
            >
              {t("maintenance:global.button.send_label")}
            </Button>
          </Tooltip>
          {steps.some((step) => step.done === false) ? (
            <Button
              block
              onClick={handleSendAnyways}
              style={{ flexBasis: "20%", flexGrow: 0 }}
            >
              {t("maintenance:global.button.send_anyways_label")}
            </Button>
          ) : null}
          <Button onClick={() => setTicketModalOpen(true)}>
            {t("maintenance:schedule.execute.ticket.create")}
          </Button>
        </Flex>
      );
    }, [stepsUpdated]);

    // return
    // loading ? (
    //   <Loading message={t("maintenance:global.loading")} />
    // ) : (
    return (
      <div style={{ background: "#f5f5f5", minHeight: "100vh" }}>
        {/* Überschrift und Steps Navigation zusammen */}
        <div
          style={{
            marginBottom: 32,
            backgroundColor: "white",
            padding: 24,
            borderRadius: 8,
          }}
        >
          <Title
            level={3}
            style={{ margin: 0, textAlign: "center", marginBottom: 24 }}
          >
            Wartungsquittierung
          </Title>

          <style>
            {`
              .black-text-steps .ant-steps-item-title {
                color: #000000 !important;
              }
            `}
          </style>
          <Steps
            current={currentStep}
            direction="horizontal"
            labelPlacement="vertical"
            onChange={(current) => {
              setCurrentStep(current);
            }}
            style={{
              color: "#000000",
            }}
            className="black-text-steps"
            items={steps.map((step: StepsObjectInScheduleExecution) => {
              return {
                key: step.id,
                title: step.title,
                disabled: step.disabled,
                status: step.done ? "finish" : "error",
                icon: step.done ? (
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#52c41a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </div>
                ) : (
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#fa8c16",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    !
                  </div>
                ),
              };
            })}
          />
        </div>

        {/* Navigation Buttons - nur anzeigen wenn notwendig */}
        {/* Entfernt - wird unter Schritt-Content platziert */}

        {/* Schritt Content */}
        {steps.length > 0 && (
          <div
            style={{
              backgroundColor: "white",
              padding: 24,
              borderRadius: 8,
              marginBottom: 32,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
            }}
          >
            <div
              style={{
                marginBottom: 20,
                padding: 16,
                backgroundColor: steps[currentStep]?.done
                  ? "#f6ffed"
                  : "#fff7e6",
                border: `2px solid ${steps[currentStep]?.done ? "#52c41a" : "#faad14"}`,
                borderRadius: 8,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: steps[currentStep]?.done ? "#52c41a" : "#fa8c16",
                  marginBottom: 8,
                }}
              >
                {steps[currentStep]?.done
                  ? "✓ Schritt abgehakt"
                  : "⚠ Schritt noch nicht abgehakt"}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: steps[currentStep]?.done ? "#389e0d" : "#d46b08",
                  marginBottom: 16,
                }}
              >
                {steps[currentStep]?.done
                  ? "Dieser Wartungsschritt wurde erfolgreich quittiert"
                  : "Bitte haken Sie diesen Schritt ab, um ihn als erledigt zu markieren"}
              </div>

              {/* Prominenter Abhak-Switch */}
              <div
                style={{
                  padding: 16,
                  backgroundColor: "white",
                  borderRadius: 8,
                  border: "2px solid #1890ff",
                  display: "inline-block",
                }}
              >
                <Flex align="center" gap={12}>
                  <Switch
                    checked={steps[currentStep]?.done || false}
                    onChange={(checked) => {
                      updateStep(steps[currentStep].id, {
                        done: checked,
                        comment: steps[currentStep]?.comment || "",
                      });
                    }}
                    style={{
                      backgroundColor: steps[currentStep]?.done
                        ? "#52c41a"
                        : undefined,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#1890ff",
                    }}
                  >
                    {steps[currentStep]?.done
                      ? "Arbeitsschritt als erledigt markiert"
                      : "Arbeitsschritt als erledigt markieren"}
                  </span>
                </Flex>
              </div>
            </div>

            <Flex vertical gap={20}>
              {(steps[currentStep]?.description ||
                (steps[currentStep]?.roles &&
                  steps[currentStep]?.roles.length > 0)) && (
                <div>
                  {steps[currentStep]?.description && (
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#333",
                        borderLeft: "4px solid #1890ff",
                        paddingLeft: 12,
                      }}
                    >
                      Beschreibung des Arbeitsschritts:
                    </span>
                  )}
                  <div
                    style={{
                      marginTop: steps[currentStep]?.description ? 12 : 0,
                    }}
                  >
                    <StepDescription step={steps[currentStep]} />
                  </div>
                </div>
              )}
              <StepExecuteContent
                step={steps[currentStep]}
                onChange={(values: StepsExecuteFormValues) => {
                  updateStep(steps[currentStep].id, values);
                }}
              />
            </Flex>

            {/* Navigation Buttons unter dem Schritt */}
            <Flex justify="space-between" style={{ marginTop: 24 }}>
              <div>
                {currentStep > 0 && (
                  <Button
                    onClick={() => {
                      setCurrentStep((prev) => prev - 1);
                    }}
                    size="large"
                    style={{
                      borderRadius: 8,
                      padding: "8px 20px",
                      fontWeight: 500,
                    }}
                  >
                    ← {t("maintenance:global.button.back_label")}
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 && (
                  <Button
                    onClick={() => {
                      setCurrentStep((prev) => prev + 1);
                    }}
                    size="large"
                    style={{
                      borderRadius: 8,
                      padding: "8px 20px",
                      fontWeight: 500,
                    }}
                  >
                    {t("maintenance:global.button.continue_label")} →
                  </Button>
                )}
              </div>
            </Flex>
          </div>
        )}

        {/* Next Schedule Tag */}
        {/* Entfernt - wird nach unten über Speicher-Buttons verschoben */}

        <Divider>Dokumente und Medien</Divider>

        <div
          style={{
            backgroundColor: "white",
            padding: 24,
            borderRadius: 8,
            marginBottom: 32,
          }}
        >
          <SelectMedia parseFiles={parseFiles} setParseFiles={setParseFiles} />
        </div>

        {props.schedule?.get("source") && foundItems.length > 0 && (
          <>
            <Divider>Material Auswahl</Divider>
            <div
              style={{
                backgroundColor: "white",
                padding: 24,
                borderRadius: 8,
                marginBottom: 32,
              }}
            >
              <Form form={form}>
                <ItemSelectionList
                  source={props.schedule?.get("source") as Source}
                />
              </Form>
            </div>
          </>
        )}

        {/* Next Schedule Tag - ganz nach unten über Speicher-Buttons */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              padding: 16,
              backgroundColor: "white",
              borderRadius: 8,
              width: "100%",
            }}
          >
            <Flex justify="space-between" align="center">
              <div>
                <span
                  style={{
                    fontSize: "16px",
                    color: "#333",
                    fontWeight: 500,
                  }}
                >
                  Nächste Wartung planen:
                </span>
                <span
                  style={{
                    marginLeft: 12,
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Klicken Sie auf den Button rechts, um die nächste Wartung zu
                  konfigurieren
                </span>
              </div>
              <NextScheduleTag schedule={props.schedule} />
            </Flex>
          </div>
        </div>

        <TicketSaveModal
          initialValues={{
            title: t("maintenance:schedule.execute.ticket.title", {
              title: props.schedule?.get("title"),
            }),
            message: {
              title: "",
              description: t(
                "maintenance:schedule.execute.ticket.description",
                {
                  currentStep: steps[currentStep]?.title,
                }
              ),
            },
            source: props.schedule?.get("source")?.id,
            priority: 50,
          }}
          open={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          onSaveSuccess={() => setTicketModalOpen(false)}
        />

        {/* Action Buttons */}
        <div
          style={{
            backgroundColor: "white",
            padding: 24,
            borderRadius: 8,
          }}
        >
          {buttons}
        </div>
      </div>
    );
  }
);
